import { Dispatch, useCallback, useState } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Field } from 'react-final-form';
import { useDispatch } from 'react-redux';

import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { KnmViolatedRequirementsNpaItem } from 'KnmPackage/KnmViolatedRequirementsNpa/components/KnmViolatedRequirementsNpaItem';
import { ErknmResultDecisionsViolatedRequirementsModal } from 'KnmErknmPackage/ErknmResultDecisionsModule/components/ErknmResultDecisionsViolatedRequirementsModal';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { getRequirementsWithNonFixedViolations } from 'KnmErknmPackage/ErknmViolationsModule/common/getRequirementsWithNonFixedViolations';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Button } from 'UIPackage/MainModule/components/Button';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { WAS_REMOVE_DUPLICATE_VIOLATIONS } from 'CorePackage/MainModule/constants/notices';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';

import styles from './DecisionResultItemContentRequirements.module.scss';

interface IDecisionResultItemContentRequirementsProps {
  watchOnly: boolean;
  isViolationFieldsWatchOnly: boolean;
  formRef: IDecisionFormRef | undefined;
  requirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  requirementEliminationsFieldName: string;
  isPaperless: boolean;
  id: string;
  onChangeFixPlanDate?: (date: string | Date) => void;
  isExpandAllInPrescription?: boolean;
  signed?: boolean;
  extractVersion?: number;
}

export const DecisionResultItemContentRequirements = ({
  watchOnly,
  isViolationFieldsWatchOnly,
  formRef,
  requirementsNpa,
  requirementEliminationsFieldName,
  isPaperless,
  onChangeFixPlanDate,
  id,
  isExpandAllInPrescription,
  signed,
  extractVersion,
}: IDecisionResultItemContentRequirementsProps) => {
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const requirementsWithUnresolvedViolations = getRequirementsWithNonFixedViolations(requirementsNpa || []);

  const removeViolatedRequirementsNpa = useCallback((npaIndex: number) => {
    remove && remove(requirementEliminationsFieldName, npaIndex);
  }, []);

  const [isViolatedRequirementsNpaModalVisible, setIsViolatedRequirementsNpaModalVisible] = useState(false);
  const openViolatedRequirementsNpaModal = useCallback(() => {
    setIsViolatedRequirementsNpaModalVisible(true);
  }, []);
  const closeViolatedRequirementsNpaModal = useCallback(() => {
    setIsViolatedRequirementsNpaModalVisible(false);
  }, []);
  const addViolatedRequirementsNpa = (items: IKnmErknmFormViolatedRequirementsNpa[]) => {
    const requirements =
      // @ts-ignore
      (formRef?.current?.getFieldState(requirementEliminationsFieldName)
        ?.value as IKnmErknmFormViolatedRequirementsNpa[]) || [];
    let hasFoundDuplicate = false;

    if (push) {
      items.forEach(item => {
        if (isPaperless) {
          const updatedItem = {
            ...item,
            violatedMandatory: item.violatedMandatory.map(vm => ({
              ...vm,
              violations: vm.violations?.map(vio => ({
                ...vio,
                fixPlanDates: [
                  {
                    isNew: true,
                    value: '',
                    guid: null,
                    dateCreate: null,
                  },
                ],
                fixPlanMethods: null,
                fixPlanExplanations: null,
                fixFactMethod: null,
                fixFactDate: null,
                fixes: null,
                fixPlanId: null,
              })),
            })),
          };

          const npaIndex = requirements.findIndex(
            req => req.npa.id === item.npa.id && req.structuralUnit.id === item.structuralUnit.id,
          );

          if (npaIndex > -1) {
            hasFoundDuplicate = true;
          } else {
            push(requirementEliminationsFieldName, updatedItem);
          }
        } else {
          push(requirementEliminationsFieldName, item);
        }
      });
    }

    if (hasFoundDuplicate) {
      dispatch(
        addNoticeAction({
          renderContent: WAS_REMOVE_DUPLICATE_VIOLATIONS,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.NPA_CHECK,
        }),
      );
    }
  };

  return (
    <>
      <FieldBlock
        label={getTitleFieldWithRedStar({
          label: 'Выявленные нарушения обязательных требований',
          field: requirementEliminationsFieldName,
          isDecision: true,
        })}
        className={styles.GroupMarginBottom}
        id={id}
        button={
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            disabled={watchOnly}
            onClick={openViolatedRequirementsNpaModal}
          />
        }
      >
        <FieldArray name={requirementEliminationsFieldName}>
          {({ fields, meta: listMeta }) => {
            return (
              <>
                {fields.map((npaName, npaIndex) => {
                  const fieldValue: IKnmErknmFormViolatedRequirementsNpa = fields.value?.[npaIndex];

                  const act = formRef?.current.getState().values.organization?.act;

                  const correspondingActRequirement = act?.violatedMandatoryRequirements?.find(
                    requirement => requirement.npa.id === fieldValue.npa.id,
                  );

                  return (
                    <Field key={npaName} name={npaName}>
                      {({ meta: itemMeta }) => {
                        const isFieldTouched = itemMeta.touched;
                        const errorField = itemMeta.error;

                        return (
                          <KnmViolatedRequirementsNpaItem
                            value={fieldValue}
                            name={npaName}
                            watchOnly={watchOnly}
                            isViolationFieldsWatchOnly={isViolationFieldsWatchOnly}
                            onRemove={() => removeViolatedRequirementsNpa(npaIndex)}
                            addMandatory={() => undefined}
                            touched={isFieldTouched}
                            error={errorField}
                            formRef={formRef}
                            isMandatoryDisabled={true}
                            isInsideDecisionsSection
                            actRequirement={correspondingActRequirement}
                            isPaperless={isPaperless}
                            onChangeFixPlanDate={onChangeFixPlanDate}
                            id={`${id}[${fieldValue.guid}]`}
                            isDecision
                            signed={signed}
                            isExpandAll={isExpandAllInPrescription}
                            extractVersion={extractVersion}
                          />
                        );
                      }}
                    </Field>
                  );
                })}
                {typeof listMeta.error === 'string' && listMeta.touched && (
                  <p className="shared-input-error shared-row__small-margin">{listMeta.error}</p>
                )}
              </>
            );
          }}
        </FieldArray>
      </FieldBlock>
      <ErknmResultDecisionsViolatedRequirementsModal
        requirementsNpa={requirementsWithUnresolvedViolations}
        visible={isViolatedRequirementsNpaModalVisible}
        onClose={closeViolatedRequirementsNpaModal}
        onSubmit={addViolatedRequirementsNpa}
      />
    </>
  );
};
